import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, MODULE_LIST, modulesForDepartments } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash, Sliders, Key } from "@phosphor-icons/react";
import { DEPARTMENTS } from "@/lib/constants";

const DEPT_LABELS = {
  vanzari: "Vânzări", office: "Office", financiar: "Financiar",
  proiectare: "Proiectare", proiectare_structuri: "Proiectare Structuri", asistent_vanzari: "Asistent Vânzări",
  achizitii: "Achiziții", gestiune: "Gestiune", productie: "Producție",
  ctc: "CTC", logistica: "Logistică", montaj: "Montaj",
  inginer_mecanic: "Inginer Mecanic",
};

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "vizitator", phone: "" });
  const [edit, setEdit] = useState(null); // user being edited for access
  const [pwUser, setPwUser] = useState(null); // user whose password is being reset
  const [pwValue, setPwValue] = useState("");

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Completează toate câmpurile");
    try {
      await api.post("/users", form);
      toast.success("Utilizator creat");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "vizitator", phone: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (uid) => {
    try { await api.delete(`/users/${uid}`); load(); toast.success("Utilizator șters"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const openEdit = (u) => setEdit({
    ...u,
    is_coordinator: !!u.is_coordinator,
    modules: Array.isArray(u.modules) ? u.modules : MODULE_LIST.map((m) => m.key),
    departments: Array.isArray(u.departments) ? u.departments : (u.department ? [u.department] : []),
  });

  const toggleDept = (key) => setEdit((e) => {
    const departments = e.departments.includes(key)
      ? e.departments.filter((k) => k !== key)
      : [...e.departments, key];
    // Modulele derivate din departamentele bifate (inclusiv accesul la hub) se calculează
    // automat; modulele adăugate manual care nu provin din niciun departament se păstrează.
    const derived = modulesForDepartments(departments);
    const allDeptModules = new Set(modulesForDepartments(DEPARTMENTS.map((d) => d.key)));
    const manualExtras = (e.modules || []).filter((m) => !allDeptModules.has(m));
    const modules = Array.from(new Set([...derived, ...manualExtras]));
    return { ...e, departments, modules };
  });

  const savePassword = async () => {
    if (!pwValue.trim() || pwValue.trim().length < 6) return toast.error("Parola trebuie să aibă minim 6 caractere");
    try {
      await api.put(`/users/${pwUser.id}`, { password: pwValue.trim() });
      toast.success(`Parolă resetată pentru ${pwUser.name}`);
      setPwUser(null);
      setPwValue("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const copyPw = async () => {
    if (!pwValue.trim()) return toast.error("Scrie întâi parola nouă");
    try { await navigator.clipboard.writeText(pwValue.trim()); toast.success("Parolă copiată în clipboard"); }
    catch { toast.error("Nu am putut copia parola"); }
  };

  const toggleModule = (key) => setEdit((e) => ({
    ...e,
    modules: e.modules.includes(key) ? e.modules.filter((k) => k !== key) : [...e.modules, key],
  }));

  const saveAccess = async () => {
    try {
      const payload = { role: edit.role, modules: edit.modules, departments: edit.departments, is_coordinator: !!edit.is_coordinator };
      if (edit.newPassword && edit.newPassword.trim()) payload.password = edit.newPassword.trim();
      await api.put(`/users/${edit.id}`, payload);
      toast.success(payload.password ? "Acces + parolă actualizate" : "Acces actualizat");
      setEdit(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-8 fade-up" data-testid="users-page">
      <div className="mb-6 flex items-end justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Administrare</p>
          <h1 className="font-head text-3xl font-extrabold tracking-tighter">Utilizatori</h1>
          <p className="mt-1 text-sm text-muted-foreground">Configurează rolul, departamentele și accesul la module pentru fiecare utilizator.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn"><Plus size={16} weight="bold" className="mr-1.5" /> Utilizator nou</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Adaugă utilizator</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              <div className="grid gap-1.5"><Label>Nume</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></div>
              <div className="grid gap-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
              <div className="grid gap-1.5"><Label>Parolă</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
              <div className="grid gap-1.5"><Label>Telefon (WhatsApp, format +40…)</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+407xxxxxxxx" data-testid="user-phone" /></div>
              <div className="grid gap-1.5">
                <Label>Rol</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="save-user-btn">Creează</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="overflow-hidden rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.02] text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">Nume</th>
              <th className="px-4 py-3 text-left font-semibold">Email</th>
              <th className="px-4 py-3 text-left font-semibold">Departament</th>
              <th className="px-4 py-3 text-left font-semibold">Rol</th>
              <th className="px-4 py-3 text-left font-semibold">Module</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3 font-semibold">{u.name}</td>
                <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                <td className="px-4 py-3 text-xs">{u.department ? <span className="rounded-full bg-sky-500/15 px-2 py-0.5 text-sky-300">{DEPT_LABELS[u.department] || u.department}</span> : <span className="text-muted-foreground/50">—</span>}</td>
                <td className="px-4 py-3"><span className="rounded-full bg-violet-500/15 px-2 py-0.5 text-xs text-violet-300">{u.title || ROLE_LABELS[u.role]}</span></td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{Array.isArray(u.modules) ? `${u.modules.length} module` : "Toate"}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => { setPwUser(u); setPwValue(""); }} className="text-muted-foreground hover:text-amber-400" data-testid={`reset-pw-${u.id}`} title="Resetează parola"><Key size={16} /></button>
                    <button onClick={() => openEdit(u)} className="text-muted-foreground hover:text-primary" data-testid={`edit-access-${u.id}`} title="Configurează acces"><Sliders size={16} /></button>
                    {u.id !== user?.id && (
                      <button onClick={() => del(u.id)} className="text-muted-foreground hover:text-red-400" data-testid={`del-user-${u.id}`}><Trash size={16} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Acces — {edit?.name}</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid max-h-[70vh] gap-4 overflow-y-auto pr-1">
              <div className="grid gap-1.5">
                <Label>Rol</Label>
                <Select value={edit.role} onValueChange={(v) => setEdit({ ...edit, role: v })}>
                  <SelectTrigger data-testid="edit-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5 rounded-lg border border-amber-500/25 bg-amber-500/[0.05] p-3">
                <Label>Resetează parola</Label>
                <Input type="password" value={edit.newPassword || ""} onChange={(e) => setEdit({ ...edit, newPassword: e.target.value })} placeholder="Lasă gol ca să păstrezi parola actuală" data-testid="edit-password" autoComplete="new-password" />
                <p className="text-xs text-muted-foreground">Setează o parolă nouă pentru acest utilizator (inclusiv administratori). Comunic-o direct persoanei și cere-i s-o schimbe.</p>
              </div>
              <div>
                <Label className="mb-2 block">Departamente cu acces</Label>
                <p className="mb-2 text-xs text-muted-foreground">Bifează departamentele — accesul la ecrane se calculează automat.</p>
                <div className="grid grid-cols-2 gap-2" data-testid="edit-departments">
                  {DEPARTMENTS.map((d) => (
                    <label key={d.key} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm">
                      <Checkbox checked={edit.departments.includes(d.key)} onCheckedChange={() => toggleDept(d.key)} data-testid={`edit-dept-${d.key}`} />
                      {d.label}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label className="mb-2 block">Module accesibile</Label>
                <div className="grid grid-cols-2 gap-2" data-testid="edit-modules">
                  {MODULE_LIST.map((m) => (
                    <label key={m.key} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm">
                      <Checkbox checked={edit.modules.includes(m.key)} onCheckedChange={() => toggleModule(m.key)} data-testid={`mod-${m.key === "/" ? "dashboard" : m.key.slice(1)}`} />
                      {m.label}
                    </label>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/[0.05] px-3 py-2.5 text-sm" data-testid="edit-coordinator-row">
                <Checkbox checked={!!edit.is_coordinator} onCheckedChange={() => setEdit((e) => ({ ...e, is_coordinator: !e.is_coordinator }))} data-testid="edit-coordinator" />
                <span><b>Coordonator de departament</b> — poate aloca intern proiecte membrilor din departamentele lui și primește rezumatul zilnic de proiecte nealocate.</span>
              </label>
            </div>
          )}
          <DialogFooter><Button onClick={saveAccess} data-testid="save-access-btn">Salvează acces</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwUser} onOpenChange={(v) => { if (!v) { setPwUser(null); setPwValue(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Resetează parola — {pwUser?.name}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div className="rounded-lg border border-amber-500/25 bg-amber-500/[0.05] p-3 text-xs text-muted-foreground">
              Setează o parolă nouă pentru <b>{pwUser?.email}</b>. Comunic-o direct persoanei și cere-i s-o schimbe după prima autentificare.
            </div>
            <div className="grid gap-1.5">
              <Label>Parolă nouă</Label>
              <Input type="text" value={pwValue} onChange={(e) => setPwValue(e.target.value)} placeholder="Minim 6 caractere" data-testid="reset-pw-input" autoComplete="new-password" />
            </div>
            <div className="grid gap-2 rounded-lg border border-border p-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Trimite parola persoanei</p>
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="text-muted-foreground">Email</span>
                <span className="truncate font-medium" data-testid="reset-pw-email">{pwUser?.email || "—"}</span>
              </div>
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="text-muted-foreground">Telefon</span>
                <span className="font-medium" data-testid="reset-pw-phone">{pwUser?.phone || "— (nesetat)"}</span>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button type="button" size="sm" variant="outline" className="h-8 text-xs" onClick={copyPw} data-testid="reset-pw-copy">Copiază parola</Button>
                {pwUser?.email && (
                  <a href={`mailto:${pwUser.email}?subject=${encodeURIComponent("Parolă HGS-OPEP resetată")}&body=${encodeURIComponent(`Bună, ${pwUser?.name || ""}. Parola ta pentru aplicația HGS-OPEP a fost resetată: ${pwValue || "(scrie parola mai sus)"}. Te rugăm s-o schimbi după prima autentificare.`)}`}
                    className="inline-flex h-8 items-center rounded-md border border-border px-3 text-xs hover:border-primary/50 hover:text-primary" data-testid="reset-pw-email-send">Trimite pe email</a>
                )}
                {pwUser?.phone && (
                  <a href={`https://wa.me/${(pwUser.phone || "").replace(/[^0-9]/g, "")}?text=${encodeURIComponent(`Bună, ${pwUser?.name || ""}. Parola ta pentru aplicația HGS-OPEP a fost resetată: ${pwValue || "(scrie parola mai sus)"}. Te rugăm s-o schimbi după prima autentificare.`)}`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex h-8 items-center rounded-md border border-emerald-500/40 px-3 text-xs text-emerald-300 hover:bg-emerald-500/10" data-testid="reset-pw-whatsapp">WhatsApp</a>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground/70">Scrie întâi parola, apoi folosește un buton. Parola se salvează doar după „Salvează parola".</p>
            </div>
          </div>
          <DialogFooter><Button onClick={savePassword} data-testid="save-pw-btn">Salvează parola</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
